import React, {useCallback, useState, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import { useList } from 'react-use';
import DatePicker from 'react-datepicker';
import {useSelector, useDispatch, shallowEqual} from 'react-redux';
import moment from 'moment';
import lodash from 'lodash';

import useDisableScrollDocument from '../../../../useHook/useDisableScrollDocument'
import Select, {SelectAsyncWrapper} from '../../../../components/Select';
import Loading from '../../../../components/loading';
import Checkbox from '../../../../components/checkbox';
import {getPlayers, getPlayerDetail} from "../../../../actions/leader-board";

const YEAR_ID = 'year_id';
const PLAYER_ID = 'player_id';
const TEAM_ID = 'team_id';
const LEAGUE_ID = 'league_id';
const START_DATE = 'game_started_date.from';
const END_DATE = 'game_started_date.to';

function ConditionModal(props) {
    const {configs, type} = props;
    useDisableScrollDocument();
    const dispatch = useDispatch();

    const [listFilter, listFilterHelper] = useList([{}]);

    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [showDate, setShowDate] = useState(false);
    const [notFoundPlayer, setNotFoundPlayer] = useState(false);
    const [loading, setLoading] = useState(false);

    const years = useSelector(state => state.metadata.years, shallowEqual);
    const teams = useSelector(state => state.metadata.teams, shallowEqual);
    const leagues = [
        { id: 1, name: 'セ・リーグ'},
        { id: 2, name: 'パ・リーグ'},
    ];

    useEffect(() => {
        if(!props.selected) return;
        const validList = props.selected.filter((filter) => filter.column !== START_DATE && filter.column !== END_DATE);
        const filterStartDate = props.selected.find((filter) => filter.column === START_DATE);
        const filterEndDate = props.selected.find((filter) => filter.column === END_DATE);
        if(filterStartDate) {
            setShowDate(true);
            setStartDate(moment(filterStartDate.value, 'YYYY/MM/DD').toDate());
            setEndDate(moment(filterStartDate.value, 'YYYY/MM/DD').toDate());
        }
        if(filterEndDate) {
            setShowDate(true);
            setStartDate(moment(filterEndDate.value, 'YYYY/MM/DD').toDate());
            setEndDate(moment(filterEndDate.value, 'YYYY/MM/DD').toDate());
        }
        const playerIds = validList.filter((filter) => filter.column === PLAYER_ID).map((filter) => filter.value);
        getPlayerInfo(playerIds).then(listPlayer => {
            const list = [];
            validList.forEach((filter) => {
                const value = configs.find(config => config.column === filter.column);
                let selected = filter.value;
                if(filter.column === YEAR_ID) {
                    selected = years.find(item => item.id === filter.value) || null;
                }
                if(filter.column === TEAM_ID) {
                    selected = teams.find(item => item.id === filter.value) || null;
                }
                if(filter.column === PLAYER_ID) {
                    selected = listPlayer.find(item => item.id === filter.value) || null;
                }
                if(filter.column === LEAGUE_ID) {
                    selected = leagues.find(item => item.id === filter.value) || null;
                }

                if(value.values) {
                    selected = value.values.find((item) => item.value === filter.value) || null;
                }
                list.push({
                    ...value,
                    selected,
                })
            });

            listFilterHelper.set([...list, {}]);
        });

    }, [props.selected]);

    const getPlayerInfo = useCallback(async (ids) => {
        if(ids.length === 0) {
            return Promise.resolve([]);
        }
        try {
            setLoading(true);
            const players = await dispatch(getPlayerDetail(ids));
            setLoading(false);
            return players;
        }catch (error) {
            return [];
        }
    }, []);


    const onAddFilter = useCallback(() => {
        listFilterHelper.push({});
    }, []);

    const onChangeStartDate = useCallback((date) => {
        setStartDate(date);
    }, []);

    const onChangeEndDate = useCallback((date) => {
        setEndDate(date);
    }, []);

    const onChangeFilter = useCallback((value, index) => {
        listFilterHelper.updateAt(index, value);
    }, []);

    const onChangeSelectedFilter = useCallback((value, filter, index) => {
        listFilterHelper.updateAt(index, {
            ...filter,
            selected: value,
        });
    }, []);

    const onLoadOptions = useCallback(async (inputValue, callback) => {
        try {
            setNotFoundPlayer(true);
            const result = await dispatch(getPlayers({
                keyword: inputValue,
                teamIds: [],
            }));
            setNotFoundPlayer(false);
            callback(result);
        }catch (error) {
            setNotFoundPlayer(true);
            callback([]);
        }
    }, []);

    const onRemoveFilter = useCallback((index) => {
        listFilterHelper.removeAt(index);
    }, []);

    const onSave = useCallback(() => {
        const listValid = listFilter.filter((item) => item.column && item.selected).map((filter) => {
            return {
                column: filter.column,
                selected: lodash.isObject(filter.selected) ? filter.selected.id || filter.selected.value : filter.selected,
            }
        });
        if(showDate) {
            listValid.push({
                column: START_DATE,
                selected: moment(startDate).format('YYYY/MM/DD'),
            });
            listValid.push({
                column: END_DATE,
                selected: moment(endDate).format('YYYY/MM/DD'),
            });
        }
        props.onSave(listValid);
    }, [listFilter, startDate, endDate, showDate]);

    const memoConfigs = useMemo(() => {
        return configs.filter(item => item.column !== 'game_started_date.from' && item.column !== 'game_started_date.to');
    }, [configs]);

    const renderItem = useCallback((filter, index) => {
        if(filter.column === TEAM_ID) {
            return <Select value={filter.selected ? filter.selected : null}
                           options={teams}
                           placeholder={filter.name}
                           getOptionValue={option => option.id}
                           getOptionLabel={option => option.team_name_s}
                           onChange={(value) => onChangeSelectedFilter(value, filter, index)}
                           styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                           menuPosition={'fixed'}
            />
        }

        if(filter.column === PLAYER_ID) {
            return <SelectAsyncWrapper
                value={filter.selected ? filter.selected : null}
                cacheOptions
                placeholder={'選手選択'}
                loadOptions={onLoadOptions}
                getOptionValue={option => option.id}
                getOptionLabel={option => option.player_name}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                menuPosition={'fixed'}
                defaultOptions={false}
                noOptionsMessage={() => notFoundPlayer ? '見つかりません' : 'プレイヤー名を入力してください'}
                loadingMessage={() => ''}
                onChange={(value) => onChangeSelectedFilter(value, filter, index)}
            />
        }

        if(filter.column === YEAR_ID) {
            return <Select value={filter.selected ? filter.selected : null}
                           options={years}
                           placeholder={filter.name}
                           getOptionValue={option => option.id}
                           getOptionLabel={option => option.name}
                           onChange={(value) => onChangeSelectedFilter(value, filter, index)}
                           styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                           menuPosition={'fixed'}
            />
        }

        if(filter.column === LEAGUE_ID) {
            return <Select value={filter.selected ? filter.selected : null}
                           options={leagues}
                           placeholder={filter.name}
                           getOptionValue={option => option.id}
                           getOptionLabel={option => option.name}
                           onChange={(value) => onChangeSelectedFilter(value, filter, index)}
                           styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                           menuPosition={'fixed'}
            />
        }

        if(filter.values) {
            return <Select value={filter.selected ? filter.selected : null}
                           options={filter.values}
                           placeholder={filter.name}
                           getOptionValue={option => option.value}
                           getOptionLabel={option => option.display}
                           onChange={(value) => onChangeSelectedFilter(value, filter, index)}
                           styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                           menuPosition={'fixed'}
            />
        }

        return <input value={filter.selected}
                      className={'dsa-input'}
                      type="text"
                      placeholder={'値'}
                      onChange={(e) => onChangeSelectedFilter(e.target.value, filter, index)}
        />
    }, []);

    return <div className={'dsa-modal-wrapper'}>
        <div className={'dsa-modal-content'}>
            <h4 className={'dsa-modal-title'}>
                絞り込み条件
            </h4>
            <div style={{minHeight: 100, maxHeight: 400, paddingTop: 15, position: 'relative'}}>
                {listFilter.map((filter, index) => {
                    return <div key={index} style={{display: 'flex', alignItems: 'center', paddingBottom: 15}}>
                        <div style={{width: 150}}>
                            <Select value={filter.column ? filter : null} options={memoConfigs}
                                    placeholder={'条件'}
                                    getOptionValue={option => option.column}
                                    getOptionLabel={option => option.name}
                                    onChange={(value) => onChangeFilter(value, index)}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    menuPosition={'fixed'}
                            />
                        </div>
                        <div style={{flex: 1, paddingLeft: 15, paddingRight: 15}}>
                            {renderItem(filter, index)}
                        </div>
                        <img alt="delete" src="/img/ic_delete2.png"
                             style={{
                                 width: 24,
                                 height: 24,
                                 cursor: 'pointer',
                             }}
                             onClick={() => {
                                 if(listFilter.length === 1) return;
                                 onRemoveFilter(index);
                             }} />
                    </div>
                })}
                <button className={'dsa-button'} onClick={onAddFilter}>
                    条件を追加
                </button>
            </div>
            {type === 'graph' && <div style={{paddingTop: 30, paddingBottom: 15, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <label style={{width: 100}}>
                    <Checkbox checked={showDate} onClick={() => setShowDate(!showDate)}/> 期間
                </label>

                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <DatePicker selected={startDate}
                                startDate={startDate}
                                maxDate={endDate}
                                endDate={endDate}
                                dateFormat="yyyy/MM/dd"
                                onChange={onChangeStartDate}
                                className="dsa-datepicker"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                menuPosition={'fixed'}
                    />
                    <span style={{paddingLeft: 10, paddingRight: 10}}>〜</span>
                    <DatePicker selected={endDate}
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                dateFormat="yyyy/MM/dd"
                                onChange={onChangeEndDate}
                                className="dsa-datepicker"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                menuPosition={'fixed'}
                    />
                </div>
            </div>}
            <div className={'dsa-footer-topic'}>
                <button className={'dsa-button-modal'}
                        onClick={props.onClose}>
                    キャンセル
                </button>
                <button className={'dsa-button-modal'}
                        onClick={onSave}>
                    更新
                </button>
            </div>
        </div>
        <Loading show={loading}/>
    </div>
}

ConditionModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};

export default ConditionModal;
